import React, { useState, useEffect } from 'react';
import { fetchUpcomingTournaments, updateTournamentBooking } from '../api/tournamentApi';
import { getApiErrorMessage } from '../utils/apiError';
import {
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import '../TournamentList.css';

// one line, cut with an ellipsis when it does not fit
const ELLIPSIS_STYLE = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// truncated cell content with the full value on hover
const EllipsisCell = ({ title, children }) => (
  <Tooltip title={title || ''}>
    <span style={ELLIPSIS_STYLE}>{children}</span>
  </Tooltip>
);

const PAGE_SIZE = 10;
const TABLE_COLUMNS = 9;
// keeps the table the same height as a full 10-row page while empty/loading
const ROW_HEIGHT = 44;
const EMPTY_TABLE_HEIGHT = PAGE_SIZE * ROW_HEIGHT;

const TournamentList = () => {
  const [tournaments, setTournaments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [bookingStatus, setBookingStatus] = useState({});
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        setLoading(true);
        const data = await fetchUpcomingTournaments(currentPage);
        const list = data?.response?.data || [];
        const reversedTournaments = [...list].reverse();
        setTournaments(reversedTournaments);
        setTotalPages(data?.response?.pagination?.last_page || 1);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    };

    loadTournaments();
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePreBookClick = (tournament) => {
    if (tournament.is_pre_booked) {
      // If the tournament is already pre-booked, change it to booked by hitting the API with updated values
      setSelectedTournament(tournament);
      setCode('');
      setDiscount(0);
      handleSubmitBooking(tournament, false);
    } else {
      // If the tournament is not pre-booked, open the dialog to initiate pre-booking
      setSelectedTournament(tournament);
      setOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCode('');
    setDiscount(0);
    setError('');
  };

  const handleSubmitBooking = async (tournament = selectedTournament, isPreBooked = true) => {
    try {
      const payload = {
        razorpay_script: isPreBooked ? code : '',
        discount: isPreBooked ? discount : 0,
        is_pre_booked: isPreBooked
      };

      const response = await updateTournamentBooking(tournament.id, payload);

      if (response.status) {
        setBookingStatus(prevStatus => ({
          ...prevStatus,
          [tournament.id]: isPreBooked ? 'Booked' : 'Available'
        }));

        // Update tournament's booking status after successful API call
        tournament.is_pre_booked = isPreBooked;

        // Close the dialog after successful submission
        handleCloseDialog();
      } else {
        alert(response?.message || response?.msg || 'Booking failed. Please try again!');
      }
    } catch (error) {
      console.error('Booking API failed:', error);
      alert(getApiErrorMessage(error, 'An error occurred while booking the tournament.'));
    }
  };

  const handleEditClick = (tournament) => {
    navigate('/create-tournament', { state: { tournament } });
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  // 24-hour clock, no AM/PM
  const formatDate = (dateString) => {
    if (!dateString) {
      return 'NA';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'NA';
    }

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).replace(',', '');
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1>Tournaments List</h1>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleBackClick}
        sx={{ marginBottom: 2 }}
      >
        Back to Dashboard
      </Button>

      {/* the table is always rendered so the layout stays identical
          between the loading, empty and populated states */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '130px' }} />
          <col style={{ width: '110px' }} />
          <col style={{ width: '160px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '200px' }} />
          <col style={{ width: '160px' }} />
          <col style={{ width: '130px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '100px' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Unique Code</th>
            <th>State</th>
            <th>Start Time</th>
            <th>Format</th>
            <th>Description</th>
            <th>Team Name</th>
            <th>Max Participants</th>
            <th>Joined Participants</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            // Loader stays until the response lands
            <tr>
              <td
                colSpan={TABLE_COLUMNS}
                style={{
                  height: EMPTY_TABLE_HEIGHT,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
              >
                <CircularProgress size={40} />
              </td>
            </tr>
          ) : tournaments.length === 0 ? (
            <tr>
              <td
                colSpan={TABLE_COLUMNS}
                style={{
                  height: EMPTY_TABLE_HEIGHT,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  fontWeight: 500,
                  color: '#666',
                }}
              >
                No Data Available
              </td>
            </tr>
          ) : (
            tournaments.map((tournament) => {
              const uniqueCode = tournament.unique_code || 'N/A';
              const format = tournament.format || 'N/A';
              const description = tournament.description || 'N/A';
              const teamName = tournament.team?.name || 'N/A';

              return (
                <tr key={tournament.id}>
                  <td>
                    <EllipsisCell title={uniqueCode}>
                      {uniqueCode}
                    </EllipsisCell>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor:
                        tournament.state === 'Completed' ? '#4caf50' :
                        tournament.state === 'In-Progress' ? '#ff9800' :
                        tournament.state === 'Scheduled' ? '#2196f3' :
                        '#f44336',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {tournament.state || 'N/A'}
                    </span>
                  </td>
                  <td>{formatDate(tournament.start_time)}</td>
                  <td>
                    <EllipsisCell title={format}>
                      {format}
                    </EllipsisCell>
                  </td>
                  <td>
                    <EllipsisCell title={description}>
                      {description}
                    </EllipsisCell>
                  </td>
                  <td>
                    <EllipsisCell title={teamName}>
                      {teamName}
                    </EllipsisCell>
                  </td>
                  <td>{tournament.max_participants ?? 0}</td>
                  <td>{tournament.participants_count ?? 0}</td>
                  <td>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleEditClick(tournament)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* hidden while loading and when there is nothing to page through */}
      {!loading && tournaments.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={loading || currentPage === 1}>
            Previous
          </button>
          <span style={{ margin: '0 10px' }}> Page {currentPage} of {totalPages} </span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={loading || currentPage === totalPages}>
            Next
          </button>
        </div>
      )}

      {/* Dialog for Pre Booking */}
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>Pre Book Tournament</DialogTitle>
        <DialogContent>
          <TextField
            label="Booked Code"
            variant="outlined"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ marginBottom: 2 }}
            required  // Mark this field as required
            error={!code && open}  // Display error if the field is empty and dialog is open
            helperText={!code && open ? 'Booked Code is required' : ''}
          />
          <TextField
            label="Discount"
            variant="outlined"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            sx={{ marginBottom: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (code) {
                handleSubmitBooking(selectedTournament); // Proceed only if code is provided
              }
            }}
            color="primary"
            disabled={!code} // Disable the submit button if the code is not provided
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TournamentList;

